import { SERVER_ROOT } from 'env';
import { withAiInputDebug } from './aiInputDebug';

const aiRoot = `${SERVER_ROOT}/api/board/ai`;

export class PlanLimitError extends Error {
  constructor(payload = {}) {
    super(payload.error || payload.message || 'Plan limit reached');
    this.name = 'PlanLimitError';
    this.code = payload.code || 'PLAN_LIMIT';
    this.plan = payload.plan;
    this.used = payload.used;
    this.limit = payload.limit;
    this.upgradeTo = payload.upgradeTo;
  }
}

function throwIfNotOk(res, data) {
  if (res.ok) return;
  const code = String(data?.code || '');
  if (res.status === 402 || code.includes('LIMIT') || code.endsWith('_REQUIRED') || data?.upgradeTo) {
    throw new PlanLimitError(data || { error: 'Plan limit reached' });
  }
  throw new Error(data?.error || res.statusText || 'AI request failed');
}

async function postJson(path, body) {
  let res;
  try {
    res = await fetch(`${aiRoot}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('Could not reach AI server. Check your connection and try again.');
  }
  const data = await res.json().catch(() => ({}));
  throwIfNotOk(res, data);
  return data;
}

async function getJson(path) {
  let res;
  try {
    res = await fetch(`${aiRoot}${path}`, {
      method: 'GET',
      credentials: 'include',
    });
  } catch {
    throw new Error('Could not reach AI server. Check your connection and try again.');
  }
  const data = await res.json().catch(() => ({}));
  throwIfNotOk(res, data);
  return data;
}

async function deleteJson(path, body) {
  let res;
  try {
    res = await fetch(`${aiRoot}${path}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('Could not reach AI server. Check your connection and try again.');
  }
  const data = await res.json().catch(() => ({}));
  throwIfNotOk(res, data);
  return data;
}

function withContext(hashtag, clientContext, extra = {}) {
  const normalizedContext = clientContext
    ? {
      ...clientContext,
      deltaOps: Array.isArray(clientContext.deltaOps)
        ? clientContext.deltaOps
          .map(op => (typeof op === 'string' ? { type: op } : op))
          .filter(op => op?.type)
        : undefined,
    }
    : null;
  return {
    hashtag,
    ...(normalizedContext ? { clientContext: normalizedContext } : {}),
    ...extra,
  };
}

/**
 * Consume a POST SSE endpoint. Calls onEvent(eventName, data) for each event.
 * Returns the `result` event payload when present.
 */
async function postSse(path, body, { onEvent, signal } = {}) {
  let res;
  try {
    res = await fetch(`${aiRoot}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new Error('Could not reach AI server. Check your connection and try again.');
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throwIfNotOk(res, data);
  }

  if (!res.body) {
    throw new Error('AI stream returned no body');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let resultPayload = null;
  let streamError = null;

  const handleBlock = (block) => {
    const lines = String(block || '').split('\n');
    let eventName = 'message';
    const dataLines = [];
    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    }
    if (!dataLines.length) return;
    const raw = dataLines.join('\n').trim();
    if (!raw) return;
    let data = {};
    try {
      data = JSON.parse(raw);
    } catch {
      data = { text: raw };
    }
    if (typeof onEvent === 'function') {
      onEvent(eventName, data);
    }
    if (eventName === 'result') resultPayload = data;
    if (eventName === 'error') {
      streamError = new Error(data.error || 'AI stream failed');
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      handleBlock(block);
    }
  }
  if (buffer.trim()) handleBlock(buffer);

  if (streamError) throw streamError;
  if (!resultPayload) {
    throw new Error('AI stream ended without a result');
  }
  return resultPayload;
}

export function postBoardAiSuggestPins(hashtag, queries, clientContext) {
  return postJson('/suggest-pins', withContext(hashtag, clientContext, { queries }));
}

export function postBoardAiPlacePending(hashtag, { activityIds, limit } = {}) {
  return postJson('/place-pending', {
    hashtag,
    ...(activityIds ? { activityIds } : {}),
    ...(limit != null ? { limit } : {}),
  });
}

export function postBoardAiPinCaseAnalyze(hashtag, payload = {}, clientContext = null) {
  return postJson('/pin-case-analyze', withContext(hashtag, clientContext, payload));
}

export function postBoardAiDigestEvent(hashtag, payload = {}) {
  return postJson('/digest-event', { hashtag, ...payload });
}

export function postBoardAiEnrichClipboard(hashtag, payload = {}) {
  return postJson('/enrich-clipboard', { hashtag, ...payload });
}

export function fetchBoardAiConversations(hashtag) {
  const encoded = encodeURIComponent(hashtag);
  return getJson(`/conversations?hashtag=${encoded}`);
}

export function createBoardAiConversation(hashtag, { title } = {}) {
  return postJson('/conversations', { hashtag, title });
}

export function deleteBoardAiConversation(hashtag, conversationId) {
  return deleteJson(`/conversations/${encodeURIComponent(conversationId)}`, { hashtag });
}

export function postBoardAiChat(hashtag, payload, clientContext) {
  return postJson('/chat', withContext(hashtag, clientContext, withAiInputDebug(payload)));
}

export function postBoardAiChatStream(hashtag, payload, clientContext, { onEvent, signal } = {}) {
  return postSse('/chat/stream', withContext(hashtag, clientContext, withAiInputDebug(payload)), { onEvent, signal });
}
